import CancelIcon from '@mui/icons-material/Cancel';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';

export type MultiSelectOption = {
  key: string;
  name: string;
  value: string;
};

export default function MultiSelect({
  id,
  options,
  label,
  selectedValues,
  setSelectedValues,
  required,
  style,
}: {
  id: string;
  options: MultiSelectOption[];
  label: string;
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  required: boolean;
  style?: React.CSSProperties;
}) {
  const theme = useTheme();

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options ?? []) map.set(o.value, o.name);
    return map;
  }, [options]);

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    // MUI sometimes returns string when autofill is involved; normalize to string[]
    const value = e.target.value;
    setSelectedValues(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <FormControl fullWidth style={style}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>

      <Select
        id={id}
        labelId={`${id}-label`}
        label={label}
        variant='outlined'
        multiple
        value={selectedValues}
        error={required ? selectedValues.length === 0 : false}
        onChange={handleChange}
        sx={{
          '& .MuiSelect-icon': { right: '0px !important' },
          '& .MuiFormLabel-root-MuiInputLabel-root': {
            left: '-15px !important',
            color: theme.palette.oceanTint.main,
          },
        }}
        renderValue={(selected) => (
          <Stack gap={1} direction='row' flexWrap='wrap'>
            {selected.map((optionId) => (
              <Chip
                key={optionId}
                label={labelByValue.get(optionId) ?? optionId}
                onDelete={() => setSelectedValues(selectedValues.filter((x) => x !== optionId))}
                deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
              />
            ))}
          </Stack>
        )}
      >
        {(options ?? []).map((o) => (
          <MenuItem key={o.key} value={o.value}>
            {o.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
